import { useMemo } from "react";
import { useProfiles } from "../query/config-queries";
import { useAuthStore } from "../stores/auth-store";
import type { ParsedProfile } from "../types/auth";

/**
 * Finds the current user's profile from the profiles array.
 * Returns null if no matching profile exists.
 */
function useParsedProfile(): ParsedProfile | null {
  const profiles = useProfiles();
  const userType = useAuthStore((s) => s.user?.userType);

  return useMemo(() => {
    if (!userType || profiles.length === 0) return null;
    return (
      profiles.find(
        (p) => p.profile.toLowerCase() === userType.toLowerCase(),
      ) ?? null
    );
  }, [profiles, userType]);
}

// --- Module Access ---

export interface ModuleAccessResult {
  /** All modules the current user can access */
  modules: string[];
  /** Check if the user can access a specific module */
  hasModule: (moduleId: string) => boolean;
}

export function useModuleAccess(): ModuleAccessResult {
  const parsed = useParsedProfile();

  const modules = useMemo(() => parsed?.modules ?? [], [parsed]);

  const hasModule = useMemo(() => {
    const set = new Set(modules);
    return (moduleId: string) => set.has(moduleId);
  }, [modules]);

  return { modules, hasModule };
}

// --- Report Access ---

export interface ReportAccessResult {
  /** All reports the current user can access */
  reports: string[];
  /** Check if the user can access a specific report */
  hasReport: (reportId: string) => boolean;
}

export function useReportAccess(): ReportAccessResult {
  const parsed = useParsedProfile();

  const reports = useMemo(() => parsed?.reports ?? [], [parsed]);

  const hasReport = useMemo(() => {
    const set = new Set(reports);
    return (reportId: string) => set.has(reportId);
  }, [reports]);

  return { reports, hasReport };
}

// --- Shortcut Access ---

export interface ShortcutAccessResult {
  /** All shortcuts the current user can access */
  shortcuts: string[];
  /** Check if the user can access a specific shortcut */
  hasShortcut: (shortcutId: string) => boolean;
}

export function useShortcutAccess(): ShortcutAccessResult {
  const parsed = useParsedProfile();

  const shortcuts = useMemo(() => parsed?.shortcuts ?? [], [parsed]);

  const hasShortcut = useMemo(() => {
    const set = new Set(shortcuts);
    return (shortcutId: string) => set.has(shortcutId);
  }, [shortcuts]);

  return { shortcuts, hasShortcut };
}

// --- MDM Access ---

/**
 * Returns true if the current user's profile has accessLevel "mdm".
 * This is the authoritative check — userType alone is unreliable
 * (e.g. SuperAdmin can have accessLevel "mdm").
 */
export function useIsMdm(): boolean {
  const parsed = useParsedProfile();
  return parsed?.accessLevel?.toLowerCase() === "mdm";
}
